"use client";

import {
  Bell,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Download,
  Check,
  Home,
  Info,
  List,
  LogOut,
  Moon,
  PawPrint,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Star,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Button } from "@astryxdesign/core/Button";
import confetti from "canvas-confetti";
import { api, localRuntime } from "../api/client";
import type { PersonPayload } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import {
  emojiTag,
  todayKicker,
  toCanonicalDateItem,
  toCanonicalSoloEventItem,
  toCanonicalSubjectItem,
  type CanonicalDateItem,
  type CanonicalSoloEventItem,
  type CanonicalSubjectItem,
} from "../features/canonical/model";
import "../styles/canonical.css";

const CONFETTI_COLORS = ["#009ec8", "#f2b705", "#ff6b6b", "#7b61ff", "#45b97c", "#ff8a3d"];

const tabs = [
  { id: "home", label: "홈", Icon: Home },
  { id: "list", label: "목록", Icon: List },
  { id: "calendar", label: "달력", Icon: Calendar },
  { id: "settings", label: "설정", Icon: Settings },
] as const;

type ListFilter = "전체" | "사람" | "반려동물" | "일정";
const favoriteExclusionsKey = "daykeeper.v1.favorite-exclusions";
const dateToIso = (date: Date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0"),
].join("-");

const openRoute = (path: string) => {
  if (Capacitor.isNativePlatform()) {
    window.location.hash = `#${path}`;
    return;
  }
  window.location.assign(path);
};

type DayKeeperPreviewProps = {
  initialActive?: (typeof tabs)[number]["id"];
  initialAddingSubject?: boolean;
  initialLoggedIn?: boolean;
  busy?: boolean;
  error?: string | null;
  guestEnabled?: boolean;
  onApple?: () => void;
  onGuest?: () => void;
};

export default function DayKeeperPreview({
  initialActive = "home",
  initialAddingSubject = false,
  initialLoggedIn = true,
  busy = false,
  error = null,
  onApple,
  onGuest,
}: DayKeeperPreviewProps) {
  const auth = useAuth();
  const [active, setActive] = useState<(typeof tabs)[number]["id"]>(initialActive);
  const [isAddingSubject, setIsAddingSubject] = useState(initialAddingSubject);
  const [isLoggedIn] = useState(initialLoggedIn);
  const [favoriteDates, setFavoriteDates] = useState<CanonicalDateItem[]>([]);
  const [dateItems, setDateItems] = useState<CanonicalDateItem[]>([]);
  const [subjectItems, setSubjectItems] = useState<CanonicalSubjectItem[]>([]);
  const [soloEventItems, setSoloEventItems] = useState<CanonicalSoloEventItem[]>([]);
  const [dataLoading, setDataLoading] = useState(initialLoggedIn);
  const [dataError, setDataError] = useState<string | null>(null);
  const [activeHighlight, setActiveHighlight] = useState(0);
  const [pendingFavoriteRemoval, setPendingFavoriteRemoval] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isHorizontalSwipe, setIsHorizontalSwipe] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const celebrationPressTimerRef = useRef<number | null>(null);
  const celebrationPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const celebrationCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const celebrationInstanceRef = useRef<ReturnType<typeof confetti.create> | null>(null);
  const ddayCardRef = useRef<HTMLElement | null>(null);
  const dragStartRef = useRef<{ pointerId: number; x: number } | null>(null);
  const touchStartRef = useRef<{
    identifier: number;
    x: number;
    y: number;
    lastX: number;
    axis: "pending" | "horizontal" | "vertical";
  } | null>(null);

  const refreshData = useCallback(async () => {
    if (!initialLoggedIn) return;
    setDataLoading(true);
    try {
      const people = await api.getPeople();
      const dateGroups = await Promise.all(people.map(async (person) => ({
        person,
        dates: await api.getPersonDates(person.id),
      })));
      const allDates = dateGroups
        .flatMap((group) => group.dates)
        .sort((a, b) => a.occurrence.daysLeft - b.occurrence.daysLeft || a.title.localeCompare(b.title));
      const allDateItems = allDates.map(toCanonicalDateItem);
      const nextDateItems = allDateItems.filter((entry) => entry.daysLeft >= 0);
      let excludedIds: string[] = [];
      try {
        const stored = window.localStorage.getItem(favoriteExclusionsKey);
        excludedIds = stored ? JSON.parse(stored) as string[] : [];
      } catch {
        excludedIds = [];
      }
      setDateItems(allDateItems);
      setFavoriteDates(nextDateItems.filter((item) => !excludedIds.includes(item.id)));
      setSubjectItems(dateGroups.map(({ person, dates }) => toCanonicalSubjectItem(person, dates)));
      setSoloEventItems(allDates
        .filter((entry) => ["dday", "custom", "appointment"].includes(entry.type))
        .map(toCanonicalSoloEventItem));
      setActiveHighlight(0);
      setDataError(null);
    } catch (cause) {
      setDataError(cause instanceof Error ? cause.message : "날짜를 불러오지 못했습니다.");
    } finally {
      setDataLoading(false);
    }
  }, [initialLoggedIn]);

  useEffect(() => {
    void refreshData();
  }, [refreshData]);

  const playCelebration = () => {
    const canvas = celebrationCanvasRef.current;
    const card = ddayCardRef.current;
    if (!canvas || !card) return false;

    if (!celebrationInstanceRef.current) {
      celebrationInstanceRef.current = confetti.create(canvas, {
        resize: true,
        useWorker: false,
      });
    }

    const canvasRect = canvas.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const origin = {
      x: (cardRect.left + cardRect.width / 2 - canvasRect.left) / canvasRect.width,
      y: (cardRect.top + cardRect.height / 2 - canvasRect.top) / canvasRect.height,
    };

    void celebrationInstanceRef.current({
      particleCount: 120,
      spread: 360,
      startVelocity: 34,
      decay: 0.9,
      gravity: 0.65,
      scalar: 0.82,
      ticks: 180,
      colors: CONFETTI_COLORS,
      origin,
      disableForReducedMotion: true,
    });
    return true;
  };

  const cancelCelebrationPress = () => {
    if (celebrationPressTimerRef.current !== null) window.clearTimeout(celebrationPressTimerRef.current);
    celebrationPressTimerRef.current = null;
    celebrationPressStartRef.current = null;
  };

  const startCelebrationPress = (clientX: number, clientY: number) => {
    if (!favoriteDates[activeHighlight]?.today) return;
    cancelCelebrationPress();
    celebrationPressStartRef.current = { x: clientX, y: clientY };
    celebrationPressTimerRef.current = window.setTimeout(() => {
      celebrationPressTimerRef.current = null;
      celebrationPressStartRef.current = null;
      playCelebration();
    }, 550);
  };

  useEffect(() => {
    const todayEvent = favoriteDates.find((item) => item.today);
    if (!todayEvent) return;

    const storageKey = `daykeeper-auto-confetti-v2-${todayEvent.iso}`;
    try {
      if (window.localStorage.getItem(storageKey)) return;
    } catch {
      // Storage can be unavailable in private browsing; the effect still runs once per mount.
    }

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        if (!playCelebration()) return;
        try {
          window.localStorage.setItem(storageKey, "1");
        } catch {
          // The animation has already played even when persistence is unavailable.
        }
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [favoriteDates]);

  const moveHighlight = (direction: -1 | 1) => {
    if (favoriteDates.length > 1) {
      setActiveHighlight((current) => (current + direction + favoriteDates.length) % favoriteDates.length);
    }
    setPendingFavoriteRemoval(null);
    setDragOffset(0);
    setIsDragging(false);
    setIsHorizontalSwipe(false);
    cancelCelebrationPress();
  };

  const removeFavorite = (itemKey: string) => {
    setFavoriteDates((current) => {
      const next = current.filter((item) => item.id !== itemKey);
      setActiveHighlight((activeIndex) => Math.min(activeIndex, Math.max(0, next.length - 1)));
      try {
        const stored = window.localStorage.getItem(favoriteExclusionsKey);
        const exclusions = stored ? JSON.parse(stored) as string[] : [];
        window.localStorage.setItem(favoriteExclusionsKey, JSON.stringify([...new Set([...exclusions, itemKey])]));
      } catch {
        // Favorite state still updates for this session when storage is unavailable.
      }
      return next;
    });
    setPendingFavoriteRemoval(null);
    cancelCelebrationPress();
  };

  const finishHighlightDrag = (pointerId: number, clientX: number) => {
    const dragStart = dragStartRef.current;
    if (!dragStart || dragStart.pointerId !== pointerId) return;

    const distance = clientX - dragStart.x;
    dragStartRef.current = null;

    if (Math.abs(distance) >= 8) {
      moveHighlight(distance < 0 ? 1 : -1);
      return;
    }

    setDragOffset(0);
    setIsDragging(false);
    setIsHorizontalSwipe(false);
  };

  const finishTouchDrag = (clientX: number) => {
    const touchStart = touchStartRef.current;
    if (!touchStart) return;

    const distance = clientX - touchStart.x;
    const shouldMove = touchStart.axis === "horizontal" && Math.abs(distance) >= 8;
    touchStartRef.current = null;

    if (shouldMove) {
      moveHighlight(distance < 0 ? 1 : -1);
      return;
    }

    setDragOffset(0);
    setIsDragging(false);
    setIsHorizontalSwipe(false);
  };

  const changeTab = (id: (typeof tabs)[number]["id"]) => {
    setActive(id);
    window.requestAnimationFrame(() => scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const saveSubject = async (values: AddSubjectValues) => {
    const payload: PersonPayload = {
      targetType: values.kind,
      name: values.name.trim(),
      relation: values.relationship.trim() || null,
      category: values.kind,
      memo: values.memo.trim() || null,
      tags: [emojiTag(values.emoji)],
    };
    const saved = await api.createPerson(payload);
    setIsAddingSubject(false);
    await refreshData();
    openRoute(`/people/${saved.id}`);
  };

  const exportData = async () => {
    const people = await api.getPeople();
    const importantDates = (await Promise.all(people.map((person) => api.getPersonDates(person.id))))
      .flat()
      .map(({ person: _person, occurrence: _occurrence, ...date }) => date);
    const payload = { people, importantDates };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `daykeeper-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const deleteAllData = async () => {
    const people = await api.getPeople();
    for (const person of people) await api.deletePerson(person.id);
    try {
      window.localStorage.removeItem(favoriteExclusionsKey);
    } catch {
      // The store itself has already been cleared.
    }
    await refreshData();
  };

  return (
    <main className="stage canonical-design-root">
      <section className={`phone${isHorizontalSwipe ? " is-horizontal-swiping" : ""}`} data-astryx-theme="matcha" aria-label="DayKeeper iOS 앱 라이브 시안">
        <canvas className="celebration-canvas" ref={celebrationCanvasRef} aria-hidden="true" />
        <div className="status" aria-hidden="true">
          <span>9:41</span>
        </div>

        {!isLoggedIn ? (
          <LoginScreen busy={busy} error={error} onApple={onApple} onGuest={onGuest} />
        ) : isAddingSubject ? (
          <AddSubjectScreen onCancel={() => setIsAddingSubject(false)} onSave={saveSubject} />
        ) : <>
        <header className="app-header">
          <div className="brand">
            <img className="brand-mark" src="/daykeeper-logo-blue.png" alt="DayKeeper" />
          </div>
          <Button
            label="대상 또는 날짜 추가"
            variant="ghost"
            isIconOnly
            icon={<Plus size={21} strokeWidth={2.2} />}
            className="icon-button"
            onClick={() => setIsAddingSubject(true)}
          />
        </header>

        <div className={`scroll-area screen-${active}`} ref={scrollAreaRef}>
          {active === "home" && <>
          <section className="date-highlight-section">
            <div className="greeting">
              <p className="kicker">{todayKicker()}</p>
            </div>

            {dataError && <div className="canonical-status canonical-status-error" role="alert">{dataError}</div>}
            {dataLoading && <div className="canonical-status" role="status">날짜를 불러오는 중...</div>}

            <div
              className={`highlight-carousel${isDragging ? " is-dragging" : ""}${isHorizontalSwipe ? " is-horizontal-swiping" : ""}`}
              onPointerDown={(event) => {
                if (!event.isPrimary || event.pointerType === "touch") return;
                dragStartRef.current = { pointerId: event.pointerId, x: event.clientX };
                startCelebrationPress(event.clientX, event.clientY);
                event.currentTarget.setPointerCapture(event.pointerId);
                setIsDragging(true);
              }}
              onPointerMove={(event) => {
                const dragStart = dragStartRef.current;
                if (!dragStart || dragStart.pointerId !== event.pointerId) return;
                const distance = Math.max(-130, Math.min(130, event.clientX - dragStart.x));
                if (Math.abs(distance) >= 8) cancelCelebrationPress();
                if (Math.abs(distance) >= 4) setIsHorizontalSwipe(true);
                setDragOffset(distance);
              }}
              onPointerUp={(event) => {
                cancelCelebrationPress();
                finishHighlightDrag(event.pointerId, event.clientX);
              }}
              onPointerCancel={(event) => {
                cancelCelebrationPress();
                finishHighlightDrag(event.pointerId, dragStartRef.current?.x ?? event.clientX);
              }}
              onTouchStart={(event) => {
                if (event.touches.length !== 1) return;
                const touch = event.touches[0];
                touchStartRef.current = {
                  identifier: touch.identifier,
                  x: touch.clientX,
                  y: touch.clientY,
                  lastX: touch.clientX,
                  axis: "pending",
                };
                startCelebrationPress(touch.clientX, touch.clientY);
                setIsDragging(true);
              }}
              onTouchMove={(event) => {
                const touchStart = touchStartRef.current;
                if (!touchStart) return;

                let touch: (typeof event.touches)[number] | null = null;
                for (let index = 0; index < event.touches.length; index += 1) {
                  if (event.touches[index].identifier === touchStart.identifier) {
                    touch = event.touches[index];
                    break;
                  }
                }
                if (!touch) return;

                const distanceX = touch.clientX - touchStart.x;
                const distanceY = touch.clientY - touchStart.y;
                touchStart.lastX = touch.clientX;
                if (Math.max(Math.abs(distanceX), Math.abs(distanceY)) >= 8) cancelCelebrationPress();

                if (touchStart.axis === "pending" && Math.max(Math.abs(distanceX), Math.abs(distanceY)) >= 4) {
                  touchStart.axis = Math.abs(distanceX) > Math.abs(distanceY) ? "horizontal" : "vertical";
                }
                if (touchStart.axis !== "horizontal") return;

                event.preventDefault();
                setIsHorizontalSwipe(true);
                setDragOffset(Math.max(-130, Math.min(130, distanceX)));
              }}
              onTouchEnd={(event) => {
                const touchStart = touchStartRef.current;
                if (!touchStart) return;
                cancelCelebrationPress();

                let clientX = touchStart.lastX;
                for (let index = 0; index < event.changedTouches.length; index += 1) {
                  if (event.changedTouches[index].identifier === touchStart.identifier) {
                    clientX = event.changedTouches[index].clientX;
                    break;
                  }
                }
                finishTouchDrag(clientX);
              }}
              onTouchCancel={() => {
                cancelCelebrationPress();
                finishTouchDrag(touchStartRef.current?.x ?? 0);
              }}
              onContextMenu={(event) => {
                if (favoriteDates[activeHighlight]?.today) event.preventDefault();
              }}
            >
              <div
                className="highlight-grid"
                role="region"
                aria-label="즐겨찾기 일정"
                aria-live="polite"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "ArrowLeft") moveHighlight(-1);
                  if (event.key === "ArrowRight") moveHighlight(1);
                }}
              >
                {favoriteDates.map((item, index) => {
                  const itemKey = item.id;
                  const stackIndex = (index - activeHighlight + favoriteDates.length) % favoriteDates.length;
                  const stackDepth = Math.min(stackIndex, 2);
                  const isActiveCard = stackIndex === 0;
                  const isVisibleCard = stackIndex <= 2;
                  const cardStyle = {
                    "--stack-x": `${stackDepth * 12}px`,
                    "--stack-y": `${stackDepth * 7}px`,
                    "--stack-scale": 1 - stackDepth * 0.035,
                    "--stack-opacity": isVisibleCard ? 1 : 0,
                    "--drag-x": `${isActiveCard ? dragOffset : 0}px`,
                    "--drag-rotate": `${isActiveCard ? (dragOffset / 130) * 3 : 0}deg`,
                    zIndex: isActiveCard ? 20 : 19 - stackDepth,
                    pointerEvents: isActiveCard ? "auto" : "none",
                  } as CSSProperties;

                  return (
                    <article
                      className="highlight-card"
                      key={`highlight-${item.id}`}
                      data-active={isActiveCard}
                      aria-current={isActiveCard ? "date" : undefined}
                      aria-hidden={!isActiveCard}
                      style={cardStyle}
                      ref={item.today ? ddayCardRef : undefined}
                    >
                    <div className="highlight-person">
                      <span aria-hidden="true">{item.emoji}</span>
                      <strong>{item.name}</strong>
                      <button
                        type="button"
                        className="favorite-button"
                        aria-label={`${item.name} 즐겨찾기 해제`}
                        aria-expanded={pendingFavoriteRemoval === itemKey}
                        aria-controls={pendingFavoriteRemoval === itemKey ? "favorite-removal-popover" : undefined}
                        onPointerDown={(event) => {
                          event.stopPropagation();
                          cancelCelebrationPress();
                        }}
                        onTouchStart={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          setPendingFavoriteRemoval(itemKey);
                        }}
                      >
                        <Star className="favorite-icon" size={14} strokeWidth={1.8} fill="currentColor" aria-hidden="true" />
                      </button>
                    </div>
                    {pendingFavoriteRemoval === itemKey && (
                      <div
                        className="favorite-popover"
                        id="favorite-removal-popover"
                        role="alertdialog"
                        aria-label="즐겨찾기 해제 확인"
                        onPointerDown={(event) => event.stopPropagation()}
                        onTouchStart={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <strong>즐겨찾기에서 해제할까요?</strong>
                        <span>해제하면 이 카드에서 사라집니다.</span>
                        <div>
                          <button type="button" onClick={() => setPendingFavoriteRemoval(null)}>취소</button>
                          <button type="button" className="remove-favorite" onClick={() => removeFavorite(itemKey)}>해제</button>
                        </div>
                      </div>
                    )}
                    <div className="highlight-date" aria-label={`${item.badge}, ${item.date}`}>
                      <b>{item.badge}</b>
                      <span>{item.date} · {item.weekday}</span>
                    </div>
                    <div className="highlight-footer">
                      <span>{item.label}</span>
                      <em>{item.relation}</em>
                    </div>
                    </article>
                  );
                })}
                {favoriteDates.length === 0 && (
                  <div className="empty-favorites">즐겨찾기한 일정이 없습니다.</div>
                )}
              </div>
              {favoriteDates.length > 0 && (
                <div className="carousel-pagination" aria-label={`즐겨찾기 일정 ${favoriteDates.length}개 중 ${activeHighlight + 1}번째`}>
                  {favoriteDates.map((item, index) => (
                    <span className={index === activeHighlight ? "is-active" : ""} key={`page-${item.id}`} />
                  ))}
                </div>
              )}
              <div className="swipe-hit-area" aria-hidden="true" />
            </div>
          </section>

          <section className="date-section">
            <div className="section-title">
              <h2>오늘</h2>
              <Bell size={16} aria-hidden="true" />
            </div>
            {dateItems.filter((item) => item.today).length > 0
              ? dateItems.filter((item) => item.today).map((item) => <DateRow item={item} key={item.id} onOpen={() => openRoute(`/important-dates/${item.id}/edit`)} />)
              : <div className="date-empty">오늘 예정된 날짜가 없습니다.</div>}
          </section>

          <section className="date-section">
            <div className="section-title">
              <h2>다가오는 날짜</h2>
              <button className="more-button" onClick={() => changeTab("list")}>전체 보기 <ChevronRight size={15} /></button>
            </div>
            <div className="date-list">
              {dateItems.filter((item) => item.daysLeft > 0).slice(0, 6).map((item) => <DateRow key={item.id} item={item} onOpen={() => openRoute(`/important-dates/${item.id}/edit`)} />)}
              {!dataLoading && dateItems.filter((item) => item.daysLeft > 0).length === 0 && <div className="date-empty">다가오는 날짜가 없습니다.</div>}
            </div>
          </section>

          <Button
            label="대상 또는 날짜 추가"
            variant="primary"
            size="lg"
            width="100%"
            icon={<Plus size={18} strokeWidth={2.5} />}
            className="primary-button"
            onClick={() => setIsAddingSubject(true)}
          />

          <div className="ad">
            <strong>광고</strong>
            <span>비개인화 배너 광고</span>
          </div>
          </>}

          {active === "list" && <ListScreen subjects={subjectItems} events={soloEventItems} loading={dataLoading} error={dataError} onOpenPerson={(id) => openRoute(`/people/${id}`)} onOpenDate={(id) => openRoute(`/important-dates/${id}/edit`)} onAdd={() => setIsAddingSubject(true)} />}
          {active === "calendar" && <CalendarScreen dates={dateItems} people={subjectItems} onOpenDate={(id) => openRoute(`/important-dates/${id}/edit`)} onAdd={() => subjectItems[0] ? openRoute(`/people/${subjectItems[0].id}/dates/new`) : setIsAddingSubject(true)} />}
          {active === "settings" && <SettingsScreen accountLabel={auth.status === "apple" ? "Apple 계정" : "Guest"} syncActive={Boolean(auth.session?.provider === "apple" && auth.session.accountSession?.entitlement.capabilities.includes("sync"))} onLogout={() => auth.logout()} onExport={exportData} onDeleteAll={deleteAllData} />}
        </div>

        <nav className="bottom-nav" aria-label="주요 메뉴" role="tablist">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active === id}
              aria-label={label}
              className={active === id ? "active" : ""}
              onClick={() => changeTab(id)}
            >
              <span className="tab-icon">
                <Icon size={22} strokeWidth={active === id ? 2.4 : 1.8} />
              </span>
              <span>{label}</span>
            </button>
          ))}
        </nav>
        </>}
      </section>
    </main>
  );
}

function DateRow({ item, onOpen }: { item: CanonicalDateItem; onOpen: () => void }) {
  return (
    <button type="button" className="date-row" onClick={onOpen}>
      <span className="avatar" aria-hidden="true">{item.emoji}</span>
      <div className="date-copy">
        <div className="name-line">
          <strong>{item.name}</strong>
          <span>{item.relation}</span>
        </div>
        <p>{item.label} · {item.date} {item.lunar && <em>음력</em>}</p>
      </div>
      <span className={item.today ? "badge today" : "badge"}>{item.badge}</span>
    </button>
  );
}

type AddSubjectValues = {
  kind: "person" | "pet";
  emoji: string;
  name: string;
  relationship: string;
  memo: string;
};

function AddSubjectScreen({ onCancel, onSave }: { onCancel: () => void; onSave: (values: AddSubjectValues) => Promise<void> }) {
  const [kind, setKind] = useState<"person" | "pet">("person");
  const [emoji, setEmoji] = useState("👤");
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [memo, setMemo] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const personEmojis = ["👤", "👩", "👨", "👵", "👴", "🧒"];
  const petEmojis = ["🐶", "🐱", "🐰", "🐹", "🐦", "🐢"];
  const relationshipOptions = kind === "person"
    ? ["가족", "배우자", "친구", "동료"]
    : ["반려견", "반려묘", "반려동물"];

  const changeKind = (nextKind: "person" | "pet") => {
    setKind(nextKind);
    setEmoji(nextKind === "person" ? "👤" : "🐶");
    setRelationship("");
  };

  return (
    <section className="add-subject-screen" aria-label="대상 추가">
      <header className="form-nav">
        <button type="button" onClick={onCancel}>취소</button>
        <h1>대상 추가</h1>
        <button type="submit" form="add-subject-form" className="save-nav" disabled={!name.trim() || saving}>{saving ? "저장 중" : "저장"}</button>
      </header>

      <form id="add-subject-form" className="add-subject-form" onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim() || saving) return;
        setSaving(true);
        setSaveError(null);
        void onSave({ kind, emoji, name, relationship, memo })
          .catch((cause) => setSaveError(cause instanceof Error ? cause.message : "대상을 저장하지 못했습니다."))
          .finally(() => setSaving(false));
      }}>
        <section className="form-section">
          <h2>어떤 대상을 기록할까요?</h2>
          <div className="subject-kind" role="radiogroup" aria-label="대상 유형">
            <button type="button" role="radio" aria-checked={kind === "person"} className={kind === "person" ? "selected" : ""} onClick={() => changeKind("person")}>
              <span><UserRound size={22} /></span><strong>사람</strong><small>가족, 친구, 소중한 사람</small>{kind === "person" && <Check size={15} />}
            </button>
            <button type="button" role="radio" aria-checked={kind === "pet"} className={kind === "pet" ? "selected" : ""} onClick={() => changeKind("pet")}>
              <span><PawPrint size={22} /></span><strong>반려동물</strong><small>함께하는 소중한 가족</small>{kind === "pet" && <Check size={15} />}
            </button>
          </div>
        </section>

        <section className="form-section icon-section">
          <h2>대표 아이콘</h2>
          <div className="emoji-picker" role="radiogroup" aria-label="대표 아이콘">
            {(kind === "person" ? personEmojis : petEmojis).map((item) => (
              <button type="button" role="radio" aria-checked={emoji === item} className={emoji === item ? "selected" : ""} key={item} onClick={() => setEmoji(item)}>{item}</button>
            ))}
          </div>
        </section>

        <section className="form-section field-section">
          <label className="form-field">
            <span>이름 <em>필수</em></span>
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder={kind === "person" ? "이름을 입력하세요" : "반려동물 이름을 입력하세요"} autoFocus />
          </label>

          <div className="form-field">
            <span>관계</span>
            <div className="relation-chips">
              {relationshipOptions.map((item) => <button type="button" className={relationship === item ? "selected" : ""} onClick={() => setRelationship(item)} key={item}>{item}</button>)}
            </div>
            <input value={relationship} onChange={(event) => setRelationship(event.target.value)} placeholder="직접 입력" aria-label="관계 직접 입력" />
          </div>

          <label className="form-field">
            <span>메모 <small>선택</small></span>
            <textarea value={memo} onChange={(event) => setMemo(event.target.value)} placeholder="기억해두고 싶은 내용을 적어보세요" maxLength={100} />
            <b>{memo.length}/100</b>
          </label>
        </section>

        <div className="add-note">
          <Calendar size={16} aria-hidden="true" />
          <p><strong>날짜는 다음 단계에서 추가할 수 있어요</strong><span>생일, 기념일, 예방접종일 등을 기록해보세요.</span></p>
        </div>
        {saveError && <div className="canonical-status canonical-status-error" role="alert">{saveError}</div>}
      </form>
    </section>
  );
}

function ListScreen({
  subjects,
  events,
  loading,
  error,
  onOpenPerson,
  onOpenDate,
  onAdd,
}: {
  subjects: CanonicalSubjectItem[];
  events: CanonicalSoloEventItem[];
  loading: boolean;
  error: string | null;
  onOpenPerson: (id: string) => void;
  onOpenDate: (id: string) => void;
  onAdd: () => void;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ListFilter>("전체");
  const normalizedQuery = query.trim().toLowerCase();
  const visibleSubjects = subjects.filter((item) => {
    const matchesFilter = filter === "전체" || filter === item.type;
    const matchesQuery = !normalizedQuery || `${item.name} ${item.relation} ${item.next}`.toLowerCase().includes(normalizedQuery);
    return matchesFilter && matchesQuery;
  });
  const visibleSoloEvents = events.filter((item) => {
    const matchesFilter = filter === "전체" || filter === "일정";
    const matchesQuery = !normalizedQuery || item.name.toLowerCase().includes(normalizedQuery);
    return matchesFilter && matchesQuery;
  });

  return (
    <section className="tab-screen list-screen" aria-label="목록">
      <label className="search-field">
        <Search size={17} aria-hidden="true" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름이나 일정 검색" aria-label="이름이나 일정 검색" />
        {query && <button type="button" onClick={() => setQuery("")} aria-label="검색어 지우기"><X size={15} /></button>}
      </label>

      <div className="filter-strip" role="tablist" aria-label="목록 필터">
        {(["전체", "사람", "반려동물", "일정"] as ListFilter[]).map((item) => (
          <button key={item} type="button" role="tab" aria-selected={filter === item} className={filter === item ? "active" : ""} onClick={() => setFilter(item)}>{item}</button>
        ))}
      </div>

      {loading && <div className="canonical-status" role="status">목록을 불러오는 중...</div>}
      {error && <div className="canonical-status canonical-status-error" role="alert">{error}</div>}

      {visibleSubjects.length > 0 && (
        <section className="list-group">
          <div className="group-label"><span>소중한 사람과 반려동물</span><em>{visibleSubjects.length}</em></div>
          <div className="subject-list">
            {visibleSubjects.map((item) => (
              <button type="button" className="subject-row" key={item.id} onClick={() => onOpenPerson(item.id)}>
                <span className="subject-avatar" aria-hidden="true">{item.emoji}</span>
                <span className="subject-main">
                  <span><strong>{item.name}</strong><em>{item.relation}</em></span>
                  <small>{item.next} · 등록된 날짜 {item.count}개</small>
                </span>
                <b className={item.badge === "D-DAY" ? "is-today" : ""}>{item.badge}</b>
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>
      )}

      {visibleSoloEvents.length > 0 && (
        <section className="list-group solo-group">
          <div className="group-label"><span>나의 일정</span><em>{visibleSoloEvents.length}</em></div>
          <div className="subject-list">
            {visibleSoloEvents.map((item) => (
              <button type="button" className="subject-row" key={item.id} onClick={() => onOpenDate(item.id)}>
                <span className="subject-avatar" aria-hidden="true">{item.emoji}</span>
                <span className="subject-main"><strong>{item.name}</strong><small>{item.date}</small></span>
                <b>{item.badge}</b><ChevronRight size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>
      )}

      {!loading && !error && visibleSubjects.length === 0 && visibleSoloEvents.length === 0 && (
        query || subjects.length || events.length
          ? <div className="search-empty"><Search size={24} /><strong>검색 결과가 없어요</strong><span>다른 이름이나 일정으로 찾아보세요.</span></div>
          : <div className="search-empty"><UserRound size={24} /><strong>아직 등록된 대상이 없어요</strong><span>소중한 사람이나 반려동물을 추가해보세요.</span><button type="button" className="empty-add-button" onClick={onAdd}><Plus size={15} /> 대상 추가</button></div>
      )}
    </section>
  );
}

function CalendarScreen({
  dates,
  people,
  onOpenDate,
  onAdd,
}: {
  dates: CanonicalDateItem[];
  people: CanonicalSubjectItem[];
  onOpenDate: (id: string) => void;
  onAdd: () => void;
}) {
  const todayIso = dateToIso(new Date());
  const [viewDate, setViewDate] = useState(() => new Date());
  const [selectedIso, setSelectedIso] = useState(todayIso);
  const calendarEvents = dates.reduce<Record<string, CanonicalDateItem[]>>((groups, item) => {
    (groups[item.iso] ??= []).push(item);
    return groups;
  }, {});
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = new Date(year, month, 1).getDay();
  const previousMonthDays = new Date(year, month, 0).getDate();
  const cells = Array.from({ length: 42 }, (_, index) => {
    const dayOffset = index - firstWeekday + 1;
    let cellYear = year;
    let cellMonth = month;
    let day = dayOffset;
    let isCurrentMonth = true;
    if (dayOffset <= 0) { cellMonth -= 1; day = previousMonthDays + dayOffset; isCurrentMonth = false; }
    if (dayOffset > daysInMonth) { cellMonth += 1; day = dayOffset - daysInMonth; isCurrentMonth = false; }
    if (cellMonth < 0) { cellMonth = 11; cellYear -= 1; }
    if (cellMonth > 11) { cellMonth = 0; cellYear += 1; }
    const iso = `${cellYear}-${String(cellMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return { day, iso, isCurrentMonth };
  });
  const selectedEvents = calendarEvents[selectedIso] ?? [];
  const selectedLabel = new Intl.DateTimeFormat("ko-KR", { month: "long", day: "numeric", weekday: "long" }).format(new Date(`${selectedIso}T12:00:00`));

  const moveMonth = (delta: number) => {
    const next = new Date(year, month + delta, 1);
    setViewDate(next);
    setSelectedIso(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-01`);
  };

  return (
    <section className="tab-screen calendar-screen" aria-label="달력">
      <div className="calendar-toolbar">
        <button type="button" aria-label="이전 달" onClick={() => moveMonth(-1)}><ChevronLeft size={19} /></button>
        <button type="button" className="month-label" aria-label="월 선택">{year}년 {month + 1}월 <ChevronDown size={14} /></button>
        <button type="button" aria-label="다음 달" onClick={() => moveMonth(1)}><ChevronRight size={19} /></button>
      </div>

      <div className="calendar-card">
        <div className="weekday-row">{["일", "월", "화", "수", "목", "금", "토"].map((day) => <span key={day}>{day}</span>)}</div>
        <div className="calendar-grid">
          {cells.map((cell) => {
            const isSelected = cell.iso === selectedIso;
            const isToday = cell.iso === todayIso;
            const eventCount = calendarEvents[cell.iso]?.length ?? 0;
            return (
              <button type="button" key={cell.iso} className={`${cell.isCurrentMonth ? "" : "outside"}${isSelected ? " selected" : ""}${isToday ? " today" : ""}`} onClick={() => setSelectedIso(cell.iso)} aria-label={`${cell.iso}${eventCount ? `, 일정 ${eventCount}개` : ""}`}>
                <span>{cell.day}</span>
                {eventCount > 0 && <i className={isSelected ? "selected-dot" : ""}>{eventCount > 1 ? eventCount : ""}</i>}
              </button>
            );
          })}
        </div>
      </div>

      <section className="selected-day-section">
        <div className="selected-day-title"><div><span>{selectedLabel}</span><strong>{selectedEvents.length ? `${selectedEvents.length}개의 일정` : "일정 없음"}</strong></div><button type="button" aria-label="이 날짜에 일정 추가" onClick={onAdd}><Plus size={18} /></button></div>
        {selectedEvents.length ? (
          <div className="selected-events">
            {selectedEvents.map((event) => (
              <button type="button" key={event.id} onClick={() => onOpenDate(event.id)}><span>{event.emoji}</span><div><strong>{event.name} · {event.label}</strong><small>{event.relation}{event.lunar ? " · 음력" : ""}</small></div><b>{event.badge}</b></button>
            ))}
          </div>
        ) : <div className="no-event">{people.length ? "이 날짜에 등록된 일정이 없습니다." : "먼저 대상을 추가한 뒤 날짜를 등록해보세요."}</div>}
      </section>
    </section>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} className={`ios-switch${checked ? " on" : ""}`} onClick={onChange}><span /></button>;
}

function SettingsScreen({
  accountLabel,
  syncActive,
  onLogout,
  onExport,
  onDeleteAll,
}: {
  accountLabel: string;
  syncActive: boolean;
  onLogout: () => Promise<void>;
  onExport: () => Promise<void>;
  onDeleteAll: () => Promise<void>;
}) {
  const [notifications, setNotifications] = useState(() => window.localStorage.getItem("daykeeper.v1.notifications-enabled") === "true");
  const [lunar, setLunar] = useState(() => window.localStorage.getItem("daykeeper.v1.show-lunar") !== "false");
  const [showDelete, setShowDelete] = useState(false);
  const [busyAction, setBusyAction] = useState<"export" | "delete" | "logout" | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void localRuntime.permission().then((permission) => {
      if (permission === "granted") setNotifications(true);
    });
  }, []);

  const toggleNotifications = async () => {
    setMessage(null);
    if (notifications) {
      setNotifications(false);
      window.localStorage.setItem("daykeeper.v1.notifications-enabled", "false");
      return;
    }
    const permission = await localRuntime.requestPermission();
    if (permission === "unsupported") {
      setMessage("알림 권한은 iPhone 또는 iPad 앱에서 설정할 수 있어요.");
      return;
    }
    const granted = permission === "granted";
    setNotifications(granted);
    window.localStorage.setItem("daykeeper.v1.notifications-enabled", String(granted));
    if (!granted) setMessage("기기 설정에서 DayKeeper 알림을 허용해 주세요.");
  };

  const toggleLunar = () => {
    const next = !lunar;
    setLunar(next);
    window.localStorage.setItem("daykeeper.v1.show-lunar", String(next));
  };

  const runAction = async (name: "export" | "delete" | "logout", action: () => Promise<void>) => {
    setBusyAction(name);
    setMessage(null);
    try {
      await action();
      if (name === "export") setMessage("데이터 파일을 저장했습니다.");
      if (name === "delete") {
        setShowDelete(false);
        setMessage("모든 데이터를 삭제했습니다.");
      }
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "요청을 처리하지 못했습니다.");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <section className="tab-screen settings-screen" aria-label="설정">
      <SettingsGroup title="알림">
        <SettingRow icon={<Bell size={17} />} title="알림 받기" description="다가오는 날짜를 미리 알려드려요" trailing={<Toggle checked={notifications} onChange={() => void toggleNotifications()} label="알림 받기" />} />
        <SettingRow icon={<Calendar size={17} />} title="기본 알림 시점" description="모든 새 일정에 적용" trailing={<span className="setting-value">7일 전</span>} hasArrow />
      </SettingsGroup>

      <SettingsGroup title="날짜">
        <SettingRow icon={<Moon size={17} />} title="음력 날짜 표시" description="음력 생일과 기념일을 함께 표시" trailing={<Toggle checked={lunar} onChange={toggleLunar} label="음력 날짜 표시" />} />
      </SettingsGroup>

      <SettingsGroup title="데이터">
        <SettingRow icon={<Cloud size={17} />} title="계정 동기화" description={syncActive ? "계정에 안전하게 동기화 중" : "현재 기기에만 저장"} trailing={<span className={`setting-value${syncActive ? " blue" : ""}`}>{syncActive ? "켜짐" : "꺼짐"}</span>} />
        <SettingRow icon={<Download size={17} />} title={busyAction === "export" ? "내보내는 중..." : "데이터 내보내기"} description="날짜 목록을 파일로 저장" hasArrow onClick={() => void runAction("export", onExport)} />
      </SettingsGroup>

      <SettingsGroup title="계정">
        <SettingRow icon={<UserRound size={17} />} title="로그인 상태" trailing={<span className="setting-value">{accountLabel}</span>} />
      </SettingsGroup>

      <SettingsGroup title="정보">
        <SettingRow icon={<ShieldCheck size={17} />} title="개인정보 보호" hasArrow />
        <SettingRow icon={<Info size={17} />} title="앱 정보" trailing={<span className="setting-value">1.0.0</span>} hasArrow />
      </SettingsGroup>

      {message && <div className="canonical-status" role="status">{message}</div>}
      <button type="button" className="logout-button" disabled={busyAction !== null} onClick={() => void runAction("logout", onLogout)}><LogOut size={17} /> {busyAction === "logout" ? "로그아웃 중..." : "로그아웃"}</button>
      <button type="button" className="delete-all" onClick={() => setShowDelete(true)}><Trash2 size={17} /> 모든 데이터 삭제</button>
      <p className="settings-footer">DAYKEEPER · MADE FOR THE DAYS THAT MATTER</p>

      {showDelete && (
        <div className="delete-overlay" role="presentation" onClick={() => setShowDelete(false)}>
          <div className="delete-sheet" role="alertdialog" aria-modal="true" aria-labelledby="delete-title" onClick={(event) => event.stopPropagation()}>
            <span className="sheet-handle" />
            <div className="delete-icon"><Trash2 size={20} /></div>
            <h2 id="delete-title">모든 데이터를 삭제할까요?</h2>
            <p>등록한 사람, 반려동물, 일정이 모두 삭제되며 되돌릴 수 없습니다.</p>
            <button type="button" className="confirm-delete" disabled={busyAction !== null} onClick={() => void runAction("delete", onDeleteAll)}>{busyAction === "delete" ? "삭제하는 중..." : "모든 데이터 삭제"}</button>
            <button type="button" className="cancel-delete" onClick={() => setShowDelete(false)}>취소</button>
          </div>
        </div>
      )}
    </section>
  );
}

function LoginScreen({
  busy,
  error,
  onApple,
  onGuest,
}: {
  busy: boolean;
  error: string | null;
  onApple?: () => void;
  onGuest?: () => void;
}) {
  return (
    <section className="login-screen" aria-label="로그인">
      <div className="login-hero">
        <div className="login-mark" aria-hidden="true">
          <img src="/daykeeper-logo-blue.png" alt="" />
        </div>
        <h1>DayKeeper 시작하기</h1>
        <p>계정으로 날짜를 안전하게 관리하거나<br />로그인 없이 바로 둘러보세요.</p>
      </div>

      <div className="login-actions" aria-label="로그인 방법" aria-busy={busy}>
        <button type="button" className="login-provider apple-provider" onClick={onApple} disabled={busy}>
          <span className="login-provider-mark apple-provider-mark" aria-hidden="true"></span>
          <span>Apple로 계속하기</span>
        </button>
        <button type="button" className="login-provider google-provider" disabled={busy}>
          <span className="login-provider-mark google-provider-mark" aria-hidden="true">G</span>
          <span>Google로 계속하기</span>
        </button>
        <button type="button" className="login-provider kakao-provider" disabled={busy}>
          <span className="login-provider-mark kakao-provider-mark" aria-hidden="true">K</span>
          <span>카카오로 계속하기</span>
        </button>
        <button type="button" className="login-guest" onClick={onGuest} disabled={busy}>로그인 없이 이용하기</button>
        {error && <p className="auth-error" role="alert">{error}</p>}
        <div className="login-legal">
          <button type="button">개인정보처리방침</button>
          <span aria-hidden="true">·</span>
          <button type="button">이용약관</button>
        </div>
      </div>
    </section>
  );
}

function SettingsGroup({ title, children }: { title: string; children: ReactNode }) {
  return <section className="settings-group"><h2>{title}</h2><div>{children}</div></section>;
}

function SettingRow({ icon, title, description, trailing, hasArrow, onClick }: { icon: ReactNode; title: string; description?: string; trailing?: ReactNode; hasArrow?: boolean; onClick?: () => void }) {
  const content = <>
      <span className="setting-icon">{icon}</span>
      <div className="setting-copy"><strong>{title}</strong>{description && <small>{description}</small>}</div>
      {trailing}{hasArrow && <ChevronRight className="setting-arrow" size={15} />}
  </>;
  return onClick
    ? <button type="button" className="setting-row setting-row-button" onClick={onClick}>{content}</button>
    : <div className="setting-row">{content}</div>;
}
